import React, { useCallback, useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import ThemeToggle from "./ui/ThemeToggle";
import NavDesktop from "./navigation/NavDesktop";
import NavMobilePanel from "./navigation/NavMobilePanel";
import { LOGO_SRC, scrollToSection } from "./navigation/navConfig";
import { useScrolledPast } from "../../hooks/useScrolledPast";
import { trackEvent } from "../../lib/track";

const goHomeTop = () => {
  // On any non-homepage route, navigate back to `/`. The visitor
  // expects the logo to be the universal "return home" anchor; a
  // silent scroll-to-top would otherwise be a dead button on
  // standalone routes (Journal, Snapshot, About, Personal Action Plan, etc.).
  if (window.location.pathname !== "/") {
    window.location.href = "/";
    return;
  }
  window.scrollTo({ top: 0, behavior: "smooth" });
  if (window.location.hash) {
    window.history.pushState(null, "", window.location.pathname);
  }
};

const Navigation = () => {
  const scrolled = useScrolledPast(24);
  const [open, setOpen] = useState(false);

  const handleLinkClick = useCallback((link) => {
    setOpen(false);
    if (link.id === "snapshot") {
      trackEvent("snapshot_nav_clicked", { surface: "nav" });
    } else {
      trackEvent("nav_link_clicked", { id: link.id });
    }
    if (link.route) {
      window.location.href = link.route;
      return;
    }
    scrollToSection(link.id);
  }, []);

  // Body-scroll-lock while the mobile drawer is open. We pin the
  // current scroll position into a CSS variable so the page doesn't
  // jump to top when the user closes the drawer.
  useEffect(() => {
    if (!open) return undefined;
    const y = window.scrollY;
    const prev = document.body.style.cssText;
    document.body.style.position = "fixed";
    document.body.style.top = `-${y}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    return () => {
      document.body.style.cssText = prev;
      window.scrollTo(0, y);
    };
  }, [open]);

  // Close the drawer when the visible URL hash changes (covers both
  // in-app anchor scrolls and back/forward navigation).
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener("hashchange", close);
    return () => window.removeEventListener("hashchange", close);
  }, [open]);

  return (
    <header
      data-testid="site-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "nav-scrolled" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between gap-6">
        <button
          data-testid="nav-logo"
          onClick={goHomeTop}
          className="flex items-center gap-3 group flex-shrink-0"
        >
          <img
            src={LOGO_SRC}
            alt="Authentic Momentum"
            className="h-10 w-10 object-contain logo-mask transition-transform duration-500 group-hover:rotate-12"
          />
          <span className="font-serif text-base tracking-[0.3em] text-themed/90 uppercase hidden 2xl:block whitespace-nowrap">
            Authentic <span className="text-gold">Momentum</span>
          </span>
        </button>

        <NavDesktop onLinkClick={handleLinkClick} />

        <div className="xl:hidden flex items-center gap-3">
          <ThemeToggle />
          <button
            data-testid="nav-mobile-toggle"
            className="text-themed/80"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && <NavMobilePanel onLinkClick={handleLinkClick} />}
    </header>
  );
};

export default Navigation;
